import { Component, Input, ViewChild, Output, EventEmitter, HostBinding, SimpleChanges, ChangeDetectionStrategy, OnInit } from '@angular/core'
import { Location } from '@angular/common'
import { Subject } from 'rxjs'
import moment from 'moment'
import { PostPreview } from './post-preview/post-preview.component'
import { postInContext, postLocked } from '../../logic/post-states'
import { queueLocked } from '../../logic/customer-states'
import { PostExtraCode } from '../../types'
import { videoPrice } from '../../utils'
import sweetAlert from '../../bmAlert'
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'
import { BaseComponent } from '../../components/base.component'
import { centsToCurrencyString } from 'src/logic/pricing'
import { SubmissionMutations } from '../submissions/submission.mutations'
import { successToast, errorToast } from 'src/toaster'
import clipboard, { DT } from 'clipboard-polyfill'
import { Customer, Post, User } from '../submissions/submission.queries'
import { EditspaceService } from 'src/services/editspace.service'

@Component({
  selector: 'post-box',
  styles: [`
    :host {
      display: block;
    },
    :host .heart-container {
      background-color: purple;
      border-radius: 50%;
      width: 18px;
      height: 18px;
    }
  `],
  template: `
    <div id="post-box-{{post.id}}" [class.expanded]="post.expanded">
      <div class="post-box-header text-disabled pl-2 p-1"
        [class.post-box-header_highlighted]="isNew"
        *ngIf="showKeyword">
        <small> {{post.keyword.phrase }} </small>
      </div>
      <hr class="m-0">
      <div class="post-box-body d-flex flex-row align-items-between"
        [class.post-box-body_highlighted]="isNew"
        (click)="onAction('read')"
        (keyup.enter)="onAction('read')"
        [tabindex]="0">
        <div class="p-0 col-auto">
          <writer-avatar
            [writer]="post.writer"
            [showManage]="true"
            [hideName]="true"
            [customerId]="customer?.id"
            [preferred] = "post.writerPreferred"
            [blocked] = "post.writerBlocked"
            *ngIf="post.writer && post.writer.canShow">
          </writer-avatar>
        </div>
        <div [class.col]="post?.writer?.canShow" [class.ml-1]="!post?.writer?.canShow">
          <div class="font-weight-bold">
            {{ post.title }}
            <i *ngIf="inContext('edited') || inContext('edits_declined')" class="small">
              ({{post.state | sentencecase:{upperFirst: false} }})
            </i>
            <post-thirty-day-message
              class="ml-1"
              *ngIf="showThirtyDayMessage"
              [postSentAt]="post.sentToCustomerAt"
              [writerName]="post.writer.shortDisplayName">
            </post-thirty-day-message>
          </div>
          <div *ngIf="post?.writer?.shortDisplayName && post?.writer?.canShow" class="small d-flex align-items-center">
            by&nbsp;
            <strong>{{post.writer.shortDisplayName}}</strong>
            <i *ngIf="post.writerPreferred" class="fas fa-heart text-purple fa-xs ml-1"></i>
            <red-dot [show]="post?.writer?.state === 'unavailable'" tooltipText="Writer is unavailable" class="ml-1" placement="top"></red-dot>
            <i *ngIf="post.writerBlocked" class="fas fa-ban text-danger ml-1"></i>
            <span *ngIf="displayDeadline">
              <span class="mx-1">|</span>Due
              <span [class.text-danger]="deadlineOverdue(post.keyword.deadline)">{{deadlinePhrasing(post.keyword.deadline)}}</span>
            </span>
            <span *ngIf="inContext('purchased')">
              <span class="mx-1">|</span>Purchased
              {{ formatted(postedDate()) }}
            </span>
            <span *ngIf="inContext('declined')">
              <span class="mx-1">|</span>Declined
              {{ formatted(declinedDate()) }}
            </span>
            <span *ngIf="inContext('withdrawn')">
              <span class="mx-1">|</span>Withdrawn
              {{ formatted(withdrawnDate()) }}
            </span>
          </div>
        </div>
        <div class="col-auto d-none d-md-flex flex-column justify-content-start align-items-end px-0">
          <div class="d-md-flex flex-row justify-content-end align-items-baseline">
            <div *ngIf="onWriterProfilePage" class="mr-2">
              <span class="badge badge-pill badge-tertiary">{{ labelFor(post.state) | titlecase }}</span>
            </div>

            <div *ngIf="inContext('purchased')">
              <ng-template [ngIf]="videoPurchased">
                <i class="fas fa-video"></i> Video purchased
              </ng-template>
              <ng-template [ngIf]="!videoPurchased">
                <button
                  (click)="videoPurchaseStep = 'buy'; preventExpansion($event)"
                  [id]="'purchased-add-video-' + post.id"
                  class="btn btn-sm secondary-outline-button bg-inherit mr-1"
                >
                  <i class="fas fa-video"></i> Add Video
                </button>
              </ng-template>
            </div>

            <div *ngIf="post?.canPin">
              <button
                mat-button
                tooltip="{{pinTooltipContent(post)}}"
                containerClass="white-tooltip"
                [disableRipple]="true"
                class="min-w-0 px-0"
                (click)="togglePinned(post); preventExpansion($event)">
                <i *ngIf="inContext('purchaseable')"
                  class="fa-regular fa-thumbtack"
                  [class.fa-solid]="post.pinned"
                  [style.width]="'30px'">
                </i>
              </button>
            </div>

            <div *ngIf="post?.writer?.canShow || post?.keyword?.canUpdate">
              <button
                mat-button
                [disableRipple]="true"
                class="min-w-0 px-0"
                [matMenuTriggerFor]="postBoxMenu"
                (click)="preventExpansion($event)">
                <i class="fas fa-ellipsis-v" [style.width]="'30px'"></i>
              </button>

              <mat-menu #postBoxMenu="matMenu" xPosition="before">
                <button mat-menu-item [routerLink]="['/dashboard', post.customerId, 'writer_profile', post?.writer?.slug]">View writer's profile</button>
                <button mat-menu-item [routerLink]="editKeywordPage">View request</button>
              </mat-menu>
            </div>
          </div>
        </div>
      </div>

      <hr [hidden]="!expanded" class="m-0">

      <div class="post-box-body" [hidden]="!expanded">
        <post-preview class="mx-3" [post]="post"></post-preview>
      </div>

      <hr class="m-0">

      <ng-template [ngIf]="showCustomer">
        <div class="mt-2 col-offset-2">
          <div class="ml-2 text-muted"><i>{{ customer?.businessName }}</i></div>
        </div>
        <hr class="my-2">
      </ng-template>

      <div class="post-box-footer" [class.post-box-footer_highlighted]="isNew">
        <div class="row">
          <div class="col-12">
            <div *ngIf="inContext('declined')" class="vertical-padding horizontal-padding">
              <post-decline-reasons
                [selectedPostDeclineReasons]="post.selectedPostDeclineReasons"
                [postDeclineComments]="post.postDeclineComments"
              ></post-decline-reasons>
            </div>
            <post-action-buttons
              [expanded]="expanded"
              [customer]="customer"
              [post]="post"
              [activeAction]="dialog"
              [isEditspace]="isEditspace"
              (action)="onAction($event)"
              (rating)="onRating($event)"
              (deprecatedStoreChange)="deprecatedStoreChange.emit($event)"></post-action-buttons>
          </div>
        </div>
        <div class="row" *ngIf="customer && post && dialog != ''">
          <div class="col-12 vertical-padding horizontal-padding">
            <div *ngIf="!multipass; else multipassPostForms">
              <purchase-post-form *ngIf="dialog === 'accept'" [post]="post" [customer]="customer" [currentUser]="currentUser" (close)="closeDialog()" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></purchase-post-form>
              <decline-post-form *ngIf="dialog === 'decline'" [post]="post" [customer]="customer" (close)="closeDialog()" (dirtyChanged)="updateDirty($event)" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></decline-post-form>
              <post-review-form *ngIf="dialog === 'review'" [post]="post" [customer]="customer" (close)="closeDialog()" (dirtyChanged)="updateDirty($event)" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></post-review-form>
              <post-wl-review-form *ngIf="dialog === 'wlApprove'" [post]="post" [customer]="customer" [action]="'approve'" (close)="closeDialog()"></post-wl-review-form>
              <post-wl-review-form *ngIf="dialog === 'wlReject'" [post]="post" [customer]="customer" [action]="'reject'" (close)="closeDialog()"></post-wl-review-form>
              <white-label-form *ngIf="dialog === 'white-label'" [post]="post" [customer]="customer" (close)="closeDialog()" (dirtyChanged)="updateDirty($event)" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></white-label-form>
              <take-back-post-form *ngIf="dialog === 'takeBack'" [post]="post" (close)="closeDialog()" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></take-back-post-form>
            </div>
            <ng-template #multipassPostForms>
              <multipass-purchase-post-form *ngIf="dialog === 'accept'" [post]="post" [customer]="customer" [currentUser]="currentUser" (close)="closeDialog()" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></multipass-purchase-post-form>
              <decline-post-form *ngIf="dialog === 'decline'" [post]="post" [customer]="customer" (close)="closeDialog()" (dirtyChanged)="updateDirty($event)" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></decline-post-form>
              <post-review-form *ngIf="dialog === 'review'" [post]="post" [customer]="customer" (close)="closeDialog()" (dirtyChanged)="updateDirty($event)" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></post-review-form>
              <post-wl-review-form *ngIf="dialog === 'wlApprove'" [post]="post" [customer]="customer" [action]="'approve'" (close)="closeDialog()"></post-wl-review-form>
              <post-wl-review-form *ngIf="dialog === 'wlReject'" [post]="post" [customer]="customer" [action]="'reject'" (close)="closeDialog()"></post-wl-review-form>
              <white-label-form *ngIf="dialog === 'white-label'" [post]="post" [customer]="customer" (close)="closeDialog()" (dirtyChanged)="updateDirty($event)" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></white-label-form>
              <take-back-post-form *ngIf="dialog === 'takeBack'" [post]="post" (close)="closeDialog()" (deprecatedStoreChange)='deprecatedStoreChange.emit($event)'></take-back-post-form>
            </ng-template>
          </div>
        </div>
      </div>
    </div>

    <video-purchase-modal
      *ngIf="videoPurchaseStep"
      [step]="videoPurchaseStep"
      [price]="prettyPrice(videoPrice)"
      (purchase)="purchaseVideo()"
      (close)="closeVideoModal()"
      [businessName]="customer?.businessName"
      [postTitle]="post?.title"></video-purchase-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PostBox extends BaseComponent implements OnInit{
  @Input() customer: Customer;
  @Input() post: Post;
  @Input() currentUser: User;
  @Input() showKeyword: boolean = true;
  @Input() showCustomer: boolean;
  @Output() closeOthers: EventEmitter<any> = new EventEmitter();
  @Output() deprecatedStoreChange: EventEmitter<number[]> = new EventEmitter<number[]>()
  @ViewChild(PostPreview) postPreview: PostPreview;
  @HostBinding('class.expanded') expanded: boolean = false
  videoPurchaseStep: 'buy' | 'survey';
  videoModalSubject: Subject<Post> = new Subject<Post>();
  dialog: string = '';
  dirty: boolean = false;
  isEditspace: boolean = false

  constructor(
    private location: Location,
    private submissionMutations: SubmissionMutations,
    private editspaceService: EditspaceService) {
      super()
  }

  ngOnInit(): void {
    this.isEditspace = this.editspaceService.isEditspace()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.post) {
      this.expanded = changes.post.currentValue.expanded
    }
  }

  get isNew(): boolean {
    return this.editspaceService.isEditspace() ? !this.post.lastViewedByWlUserAt : !this.post.lastViewedByAgencyAt
  }

  get videoPrice(): number {
    return videoPrice(this.customer)
  }

  get videoPurchased(): boolean {
    return !this.onWriterProfilePage && this.bpsEventCodes.includes('video')
  }

  get bpsEventCodes(): PostExtraCode[] {
    return (this.post.bpsEvents || []).map(bpsEvent => bpsEvent.task.code as PostExtraCode)
  }

  get editKeywordPage(): any[]|string {
    return ['/dashboard', this.post.customerId, 'topics', this.post.keywordId, 'edit']
  }

  get showThirtyDayMessage(): boolean {
    return postInContext(this.post, 'queued') && this.sentOver30DaysAgo && this.post.writer.canShow
  }

  get sentOver30DaysAgo(): boolean {
    const thirtyDaysAgo = moment().subtract(30, 'days')
    return moment(this.post.sentToCustomerAt).isBefore(thirtyDaysAgo)
  }

  get displayDeadline(): boolean {
    return this.inContext('queued') && !!this.post.keyword.deadline
  }

  get onWriterProfilePage(): boolean {
    return !!(this.location && this.location.path() && this.location.path().match('writer_profile'))
  }

  get multipass(): boolean {
    return this.post?.keyword?.multipass
  }

  deadlinePhrasing(deadlineString: string): string {
    const deadline = moment(deadlineString)
    if (deadline.isBetween(moment(), moment().add(1, 'week'))) {
      return `${deadline.fromNow()}`
    } else {
      return `${deadline.format('MMMM D')}`
    }
  }

  deadlineOverdue(deadlineString: string): boolean {
    const deadline = moment(deadlineString)
    return deadline.isBefore(moment())
  }

  inContext(context: string): boolean {
    return postInContext(this.post, context, this.isEditspace)
  }

  postedDate(): Date {
    return new Date(this.post.postedDate)
  }

  declinedDate(): Date {
    return new Date(this.post.declinedDate)
  }

  withdrawnDate(): Date {
    return new Date(this.post.withdrawnDate)
  }

  onRating(value: number): void {
    this.submissionMutations.updateRating({ customerId: this.post.customerId, id: this.post.id, updates: { value } }).then(() => {
      successToast('Rating updated')
    }).catch(() => {
      errorToast('Oops something went wrong. Try again.')
    })
  }

  writerSuspended(): boolean {
    return this.post && this.post.writer && this.post.writer.suspended
  }

  onToggleExpanded(expanded: boolean = !this.expanded): void {
    if (postInContext(this.post, 'expandable')) {
      this.submissionMutations.togglePostExpanded({ id: this.post.id, expanded })
      if (expanded) {
        this.submissionMutations.updatePost({ post: this.post, transition: 'view' })
      }
    }
  }

  // new, edited, unread, purchased
  labelFor(postState: string): string {
    if (postState === 'new') {
      return this.sentOver30DaysAgo ? 'Unread' : 'New'
    } else if (postState === 'posted') {
      return 'Purchased'
    } else if (postState === 'edits_declined') {
      return 'Edits Declined'
    } else {
      return postState
    }
  }

  formatted(date: Date): string {
    return moment(date).format('MMMM D, YYYY')
  }

  // these are now pseudo-transitions in the back end - pin/unpin methods
  togglePinned(post: Post): Promise<Post> {
    if (post.pinned) {
      return this.submissionMutations.updatePost({ post, transition: 'unpin' })
    } else {
      return this.submissionMutations.updatePost({ post, transition: 'pin' })
    }
  }

  pinTooltipContent(post: Post): string {
    if (!post.pinned) {
      return `Pin this submission to tell ${post?.writer?.shortDisplayName || 'the writer'} you intend to purchase it soon.`
    } else {
      return `Pinned! You’ve notified ${post?.writer?.shortDisplayName || 'the writer'} that you intend to purchase their submission.`
    }
  }

  _copyContentToClipboard(): void {
    const dataType: DT = new DT()
    dataType.setData('text/plain', this.post.content)
    dataType.setData('text/html', this.post.content)
    clipboard.write(dataType).then(() => {
      successToast('Copied!')
    }).catch(() => {
      errorToast('Something went wrong, please try again with the post expanded')
    })

  }

  onAction(action: string): void {
    if (action === 'read') {
      this.onToggleExpanded()
    } else if (action === 'copy') {
      this._copyContentToClipboard()
    } else {
      if (!this.dirty) {
        if (this.dialog === action) {
          this.dialog = ''
        } else {
          this.dialog = action
          setTimeout(() => {
            const box = <HTMLElement>document.getElementById(`post-box-${this.post.id}`)
            if (box) {
              const forms = box.getElementsByTagName(`form`)
              if (forms) {
                const submitButton = forms.item(0)
                if (submitButton) {
                  scrollIntoViewIfNeeded(submitButton, {easing: 'ease', duration: 40, offset: {bottom: 40}})
                }
                else {
                  //TODO for white label
                  const buttons = box.getElementsByClassName('close-button')
                  if (buttons) {
                    const closeButton = buttons[0]
                    if (closeButton) {
                      scrollIntoViewIfNeeded(closeButton, {easing: 'ease', duration: 40, offset: {bottom: 40}})
                    }
                  }
                }
              }
            }
          }, 0)
        }
      } else {
        sweetAlert.confirm({text: 'Are you sure? All unsaved changes will be lost.'}).then(confirm => {
          if (confirm) {
            this.updateDirty(false)
            this.dialog = (this.dialog === action ? '' : action)
          }
        })
      }
    }
  }

  closeDialog(): void {
    this.dialog = ''
  }

  updateDirty(isDirty: boolean): void {
    if (this.dirty !== isDirty) {
      this.dirty = isDirty
    }
  }

  queueLocked(): boolean {
    return queueLocked(this.customer)
  }

  postLocked(): boolean {
    return postLocked(this.post.submissionPosition, this.customer)
  }

  purchaseVideo(): void {
    if (!this.bpsEventCodes.includes('video')) {
      this.submissionMutations.purchasePostExtra({postId: this.post.id, code: 'video'}).then(() => {
        this.videoPurchaseStep = 'survey'
      })
    }
  }

  closeVideoModal(): void {
    this.videoModalSubject.next(this.post)
    this.videoModalSubject.complete()
    this.videoPurchaseStep = null
  }

  prettyPrice(cents: number): string {
    return centsToCurrencyString(cents, { truncateZeroCents: true })
  }

  // Clicking on the buttons inside the larger, expand section, shouldn't expand
  preventExpansion(event: Event): void {
    event.stopPropagation()
  }

}
